import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { supabase } from "../lib/supabase";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../common/SafeIcon";
import { useAuth } from "../contexts/AuthContext";
 
const {
  FiArrowLeft,
  FiHome,
  FiMessageCircle,
  FiUpload,
  FiList,
  FiCheck,
  FiUserPlus,
  FiDownload,
  FiFile,
  FiFileText,
  FiImage,
  FiVideo
} = FiIcons;
 
const RequestDetails = () => {
  const { id } = useParams();
  const { user, session } = useAuth();
 
  const [role, setRole] = useState("user");
  const [myProviderId, setMyProviderId] = useState(null); // Track the current user's ID in the 'providers' table
  const [request, setRequest] = useState(null);
  const [customerRequests, setCustomerRequests] = useState([]);
  const [customerMedia, setCustomerMedia] = useState([]);
  const [customerMessages, setCustomerMessages] = useState([]);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [images, setImages] = useState([]);
  const [progressSteps, setProgressSteps] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const chatEndRef = useRef(null);
 
  // provider controls
  const [file, setFile] = useState(null);
  const [newStep, setNewStep] = useState("");
  const [newStepDesc, setNewStepDesc] = useState("");
  const [isCompleting, setIsCompleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
 
  // admin controls
  const [showAssignProvider, setShowAssignProvider] = useState(false);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);


  const navigate = useNavigate();
  // -----------------------------
  // FETCH PROVIDERS (ADMIN ONLY)
  // -----------------------------
  const fetchProviders = async () => {
    try {
      // LOGIC CHANGE: Fetch from 'providers' table instead of 'profiles'
      const { data, error } = await supabase
        .from("providers")
        .select("id, full_name, email")
        .eq("is_active", true);
 
      if (error) throw error;
 
      setProviders(data || []);
    } catch (err) {
      console.error("Error fetching providers:", err);
    }
  };
 
  // -----------------------------
  // ASSIGN PROVIDER (ADMIN ONLY)
  // -----------------------------
  const assignProvider = async () => {
    if (!selectedProvider) {
      alert("Select a provider first");
      return;
    }
 
    try {
      const { error } = await supabase
        .from("service_requests")
        .update({ provider_id: selectedProvider })
        .eq("id", id);
 
      if (error) throw error;
 
      alert("Provider assigned successfully!");
 
      // Update UI
      setRequest((prev) => ({ ...prev, provider_id: selectedProvider }));
      setShowAssignProvider(false);
      fetchData(); // Refresh to get the provider name join
    } catch (err) {
      console.error("Failed to assign provider:", err);
    }
  };
 
  // -----------------------------
  // FETCH ALL REQUEST PAGE DATA
  // -----------------------------
  const fetchData = async () => {
    setLoading(true);
    try {
      // 1. Fetch role and my provider identity
      if (user?.id) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("role")
          .eq("id", user.id)
          .single();
        setRole(profile?.role || "user");

        // Check if this user is a provider and get their providers.id
        const { data: provRec } = await supabase
  .from("providers")
  .select("id,user_id")
  .eq("user_id", user.id)
  .eq("is_active", true)
  .maybeSingle();

if (provRec) {
  setMyProviderId(provRec.id);
}
      }

      // 2. Request with Join to get Provider Name
      // LOGIC CHANGE: Fetching provider info from the providers table join
      const { data: req } = await supabase
        .from("service_requests")
        .select(`
          *,
          provider:providers(full_name)
        `)
        .eq("id", id)
        .single();
      setRequest(req);

      // 3. Images
      const { data: imgs } = await supabase
        .from("request_images")
        .select("*")
        .eq("request_id", id)
        .order("created_at", { ascending: true });
      setImages(imgs || []);

      // 4. Steps
      const { data: steps } = await supabase
        .from("request_progress")
        .select("*")
        .eq("request_id", id)
        .order("step_number", { ascending: true });
      setProgressSteps(steps || []);

      // 5. Chats
      const { data: chats } = await supabase
        .from("request_messages")
        .select("*")
        .eq("request_id", id)
        .order("created_at", { ascending: true });
      setChatMessages(chats || []);
    } catch (err) {
      console.error("Error fetching details:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id, user?.id]);
 
  // -----------------------------
  // REALTIME SUBSCRIPTIONS
  // -----------------------------
  useEffect(() => {
    if (!id) return;
 
    const chatChannel = supabase
      .channel(`chat_${id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "request_messages", filter: `request_id=eq.${id}` },
        (payload) => setChatMessages((prev) => [...prev, payload.new])
      )
      .subscribe();
 
    return () => {
      supabase.removeChannel(chatChannel);
    };
  }, [id]);
 
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);
 
 
  // -----------------------------
  // SEND MESSAGE
  // -----------------------------
  const sendMessage = async () => {
    if (!newMessage.trim()) return;
 
    const msg = {
      request_id: id,
      message: newMessage.trim(),
      sender_id: user.id, // LOGIC CHANGE: sender_id must be a UUID (user.id)
      sender_role: role,
      created_at: new Date().toISOString(),
    };
 
    setChatMessages((prev) => [...prev, { ...msg, id: Date.now() }]);
    setNewMessage("");
 
    await supabase.from("request_messages").insert([msg]);
  };
 
  // -----------------------------
  // MEDIA HELPERS
  // -----------------------------
  const getMediaUrl = (url) => {
    if (!url) return "";
    if (url.includes("/functions/v1/upload-request-image")) {
      const token = session?.access_token || "";
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || "";
      return `${url}&token=${encodeURIComponent(token)}&apikey=${encodeURIComponent(anonKey)}`;
    }
    return url;
  };
 
  const isVideoUrl = (url) => {
    if (!url) return false;
    try {
      const urlObj = new URL(url);
      const key = urlObj.searchParams.get("key") || urlObj.pathname;
      const extension = key.split(".").pop().split("?")[0].toLowerCase();
      return ["mp4", "webm", "ogg", "mov", "m4v"].includes(extension);
    } catch {
      const extension = url.split(".").pop().split("?")[0].toLowerCase();
      return ["mp4", "webm", "ogg", "mov", "m4v"].includes(extension);
    }
  };

  const getFileExtension = (url) => {
  if (!url) return "";

  try {
    const urlObj = new URL(url);
    const key =
      urlObj.searchParams.get("key") ||
      urlObj.pathname;

    return key.split(".").pop().toLowerCase();
  } catch {
    return url.split(".").pop().toLowerCase();
  }
};

const getFileIcon = (url) => {
  const ext = getFileExtension(url);

  if (
    ["jpg", "jpeg", "png", "gif", "webp"].includes(ext)
  )
    return FiImage;

  if (
    ["mp4", "mov", "avi", "webm"].includes(ext)
  )
    return FiVideo;

  if (
    ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx"]
      .includes(ext)
  )
    return FiFileText;

  return FiFile;
};


 
  // -----------------------------
  // UPLOAD
  // -----------------------------
  const uploadImage = async () => {
    if (!file) return alert("Select file first");
    setUploading(true);
 
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("request_id", id);
 
      const { data, error } = await supabase.functions.invoke("upload-request-image", {
        body: formData,
      });
 
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Upload failed");
 
      const proxyUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-request-image?key=${encodeURIComponent(data.key)}`;
 
      const { data: inserted, error: insertErr } = await supabase
        .from("request_images")
        .insert([{ request_id: id, image_url: proxyUrl, uploaded_by: user.id }])
        .select();
 
      if (insertErr) throw insertErr;
      if (inserted && inserted.length > 0) setImages((prev) => [...prev, inserted[0]]);
 
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      alert("Upload failed: " + err.message);
    } finally {
      setUploading(false);
    }
  };
 
  // -----------------------------
  // PROGRESS ACTIONS
  // -----------------------------
  const addStep = async () => {
    if (!newStep.trim()) return;
    const newEntry = {
      request_id: id,
      title: newStep,
      description: newStepDesc,
      step_number: progressSteps.length + 1,
      created_by: user.id,
      created_at: new Date().toISOString(),
    };
    const { data } = await supabase.from("request_progress").insert([newEntry]).select();
    if (data) setProgressSteps([...progressSteps, data[0]]);
    setNewStep("");
    setNewStepDesc("");
  };
 
  const markComplete = async () => {
    setIsCompleting(true);
    await supabase.from("service_requests").update({ status: "completed" }).eq("id", id);
    setRequest((r) => ({ ...r, status: "completed" }));
    setIsCompleting(false);
  };

  
 
  const formatTime = (iso) => new Date(iso).toLocaleString();
 
  // LOGIC CHANGE: Compare the request.provider_id (UUID from providers table)
  // with the current user's provider entry ID.

  const isAssignedProvider = myProviderId && request?.provider_id && request.provider_id === myProviderId;


  const canModify =
    role === "admin" || myProviderId
 
  if (loading)
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-indigo-600 border-b-4 rounded-full" />
      </div>
    );
 
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 p-6">
      <div className="max-w-6xl mx-auto">
 
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
  onClick={() => navigate(-1)}
  className="flex items-center text-indigo-600"
>
  <SafeIcon icon={FiArrowLeft} className="mr-2" />
  Back
</button>
        </div>
 
        {/* Request Card */}
        <div className="bg-white p-8 rounded-2xl shadow border">
          <div className="flex items-center mb-4">
            <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center mr-4">
              <SafeIcon icon={FiHome} className="text-indigo-600 text-2xl" />
            </div>
 
            <div>
              <h1 className="text-3xl font-bold">
  {request.service_type
    ?.replace(/-/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())}
</h1>
 
              <p>Status: <span className="font-semibold">{request.status}</span></p>
 
              {request.provider_id && (
                <p className="text-sm text-gray-500">
                  Assigned Provider: {request.provider?.full_name || request.provider_id}
                </p>
              )}
            </div>
          </div>
 
          <p><strong>Description:</strong> {request.description}</p>
 
          {/* Admin Assign Provider Button */}
          {role === "admin" && (
            <button
              onClick={() => {
                setShowAssignProvider(true);
                fetchProviders();
              }}
              className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg"
            >
              <SafeIcon icon={FiUserPlus} className="mr-2 inline" />
              {request.provider_id ? "Change Provider" : "Assign Provider"}
            </button>
          )}
        </div>
 
        {/* 2 Columns Layout */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
 
          {/* LEFT: Progress + Images */}
          <div className="bg-white p-6 rounded-2xl shadow border">
            <div className="flex justify-between items-center mb-4">
  <h3 className="text-xl font-semibold">
    Photos & Files
  </h3>

  
</div>

<div
  id="all-files"
  className="bg-white p-6 rounded-2xl shadow border mt-6"
>
  <h3 className="text-xl font-bold mb-4">
    All Uploaded Files
  </h3>

  {images.length === 0 ? (
    <p className="text-gray-500">
      No files uploaded
    </p>
  ) : (
    <div className="space-y-3">
      {images.map((file) => {
        const fileUrl =
          getMediaUrl(file.image_url);

        const Icon =
          getFileIcon(file.image_url);

        return (
          <div
            key={file.id}
            className="border rounded-lg p-4 flex items-center justify-between"
          >
            <div className="flex items-center gap-4">
              <SafeIcon
                icon={Icon}
                className="text-2xl text-indigo-600"
              />

              <div>
                <div className="font-medium">
                  File Attachment
                </div>

                <div className="text-xs text-gray-500">
                  Uploaded:
                  {" "}
                  {new Date(
                    file.created_at
                  ).toLocaleString()}
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 bg-gray-200 rounded"
              >
                Open
              </a>

              <a
                href={fileUrl}
                download
                className="px-3 py-2 bg-green-600 text-white rounded flex items-center gap-2"
              >
                <SafeIcon
                  icon={FiDownload}
                />
                Download
              </a>
            </div>
          </div>
        );
      })}
    </div>
  )}
</div>
            {/*<h3 className="text-xl font-semibold mb-4">Photos & Progress</h3>*/}
 
            {/* IMAGES & VIDEOS }
            <div className="grid grid-cols-2 gap-3 mb-6">
              {images.map((img) => {
                const mediaUrl = getMediaUrl(img.image_url);
                const isVideo = isVideoUrl(img.image_url);
 
                return (
                  <div key={img.id} className="border rounded overflow-hidden flex flex-col justify-between bg-slate-50">
                    <div className="flex-1 flex items-center justify-center bg-black min-h-[160px]">
                      {isVideo ? (
                        <video
                          src={mediaUrl}
                          controls
                          className="max-h-[160px] w-full object-contain"
                        />
                      ) : (
                        <img
                          src={mediaUrl}
                          alt="Request attachment"
                          className="max-h-[160px] w-full object-contain"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://placehold.co/600x400?text=Error+Loading+Media";
                          }}
                        />
                      )}
                    </div>
                    <div className="text-xs p-2 border-t bg-white text-gray-500">
                      {formatTime(img.created_at)}
                    </div>
                  </div>
                );
              })}
            </div>
 
            {/* PROGRESS */}
            <h4 className="text-lg font-semibold mb-2">Work Progress</h4>
 
            {progressSteps.map((st) => (
              <div key={st.id} className="border p-3 rounded mb-3 bg-gray-50">
                <div className="flex justify-between">
                  <div className="font-medium">{st.title}</div>
                  <div className="text-xs text-gray-500">#{st.step_number}</div>
                </div>
                <p>{st.description}</p>
              </div>
            ))}

            


 
            {/* Actions (Admin & Provider only) */}
            {canModify && (
              <div className="mt-4 border-t pt-4">
                <h4 className="text-lg font-semibold mb-2">Update Request</h4>
 
                <input
                  type="text"
                  value={newStep}
                  onChange={(e) => setNewStep(e.target.value)}
                  className="w-full border p-2 rounded mb-2 text-sm"
                  placeholder="New Step Title"
                />
 
                <textarea
                  value={newStepDesc}
                  onChange={(e) => setNewStepDesc(e.target.value)}
                  className="w-full border p-2 rounded mb-2 text-sm"
                  placeholder="Description"
                />
 
                <button className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded transition-colors text-sm" onClick={addStep}>
                  Add Step
                </button>
 
                <div className="mt-4 p-4 border border-dashed border-gray-300 rounded-xl bg-slate-50">
                  <p className="text-sm font-medium text-gray-700 mb-2">Upload Images, Videos, Documents, PDFs, Excel, Word Files</p>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="*"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-md file:border-0
                      file:text-sm file:font-semibold
                      file:bg-indigo-50 file:text-indigo-700
                      hover:file:bg-indigo-100"
                    disabled={uploading}
                  />
                  <button
                    className={`mt-3 w-full px-4 py-2 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 ${
                      uploading || !file
                        ? "bg-indigo-300 cursor-not-allowed"
                        : "bg-indigo-600 hover:bg-indigo-700"
                    }`}
                    onClick={uploadImage}
                    disabled={uploading || !file}
                  >
                    {uploading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Uploading to S3...
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiUpload} />
                        Upload Media
                      </>
                    )}
                  </button>
                </div>
 
                <button
                  className="mt-4 w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors text-sm"
                  onClick={markComplete}
                  disabled={isCompleting}
                >
                  {isCompleting ? "Completing..." : "Mark Complete"}
                </button>
              </div>
            )}
          </div>
 
          {/* RIGHT: Chat */}
          <div className="bg-white p-6 rounded-2xl shadow border flex flex-col h-[600px]">
 
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <SafeIcon icon={FiMessageCircle} className="text-indigo-600 mr-2" />
              Chat
            </h3>
 
            <div className="flex-1 overflow-y-auto border rounded p-3 bg-gray-50 space-y-4">
              {chatMessages.map((msg) => {
                const outgoing = msg.sender_id === user.id; // UPDATED to use UUID
                return (
                  <div key={msg.id} className={`flex ${outgoing ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[70%] p-3 rounded-xl ${
                        outgoing ? "bg-indigo-600 text-white" : "bg-white shadow"
                      }`}
                    >
                      
 
                      <div>{msg.message}</div>
 
                      <div className="text-xs opacity-60 mt-1">
                        {formatTime(msg.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
 
              <div ref={chatEndRef} />
            </div>
 
            <div className="mt-4 flex gap-2">
              <input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                className="flex-1 border p-2 rounded"
                placeholder="Type message..."
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              />
              <button className="px-4 py-2 bg-indigo-600 text-white rounded" onClick={sendMessage}>
                Send
              </button>
            </div>
          </div>
        </div>
 
        {/* ASSIGN PROVIDER MODAL */}
        {showAssignProvider && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-md">
              <h2 className="text-xl font-bold mb-4">Assign Provider</h2>
 
              <select
                className="w-full border p-2 rounded mb-4"
                value={selectedProvider || ""}
                onChange={(e) => setSelectedProvider(e.target.value)}
              >
                <option value="">Select Provider</option>
                {providers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.full_name} ({p.service_type})
                  </option>
                ))}
              </select>
 
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowAssignProvider(false)}
                  className="px-4 py-2 bg-gray-300 rounded"
                >
                  Cancel
                </button>
 
                <button
                  onClick={assignProvider}
                  className="px-4 py-2 bg-indigo-600 text-white rounded"
                >
                  Assign
                </button>
              </div>
            </div>
          </div>
        )}
 
      </div>
    </div>
  );
};
 
export default RequestDetails;